import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { connect } from 'react-redux'
import Styles from './TopUsersCard.module.css'
import { Colors } from 'Theme'
import PropTypes from 'prop-types'
import AnalyticsSelectors from 'Stores/Analytics/Selectors'
import AnalyticsActions from 'Stores/Analytics/Actions'
import IFText from 'Components/IFText/IFText'
import { useTranslation } from 'react-i18next'
import RequestState from 'Enums/RequestState'
import IFSkeleton from 'Components/IFSkeleton/IFSkeleton'
import IFTable from 'Components/IFTable/IFTable'
import ReportIcon from '@mui/icons-material/Report'
import { getEnergyUnit } from 'Utils/Calculations'
import { useNavigate } from 'react-router-dom'
import UserActions from 'Stores/User/Actions'
import { encodeObjectToBase64 } from 'Utils/Base64Functions'
import history from 'history/browser'
import AuthSelectors from 'Stores/Auth/Selectors'

const TopUsersCard = ({
  fetchTopUsers,
  topUsers,
  filter,
  fetchTopUsersRequestState,
  setUsersFilter,
  admin,
}) => {
  const { t } = useTranslation()
  const tableRef = useRef(null)
  const navigate = useNavigate()

  useEffect(() => {
    fetchTopUsers(filter)
  }, [filter])

  const [failedRequest, setFailedRequest] = useState(false)
  useEffect(() => {
    if (
      fetchTopUsersRequestState === RequestState.ERROR_0_NETWORK ||
      fetchTopUsersRequestState === RequestState.ERROR_400_OCCURRED ||
      fetchTopUsersRequestState === RequestState.ERROR_401_OCCURRED ||
      fetchTopUsersRequestState === RequestState.ERROR_403_OCCURRED ||
      fetchTopUsersRequestState === RequestState.ERROR_409_OCCURRED ||
      fetchTopUsersRequestState === RequestState.ERROR_UNKNOWN_OCCURRED
    ) {
      setFailedRequest(true)
    }
    if (fetchTopUsersRequestState === RequestState.SUCCEEDED) {
      setFailedRequest(false)
    }
  }, [fetchTopUsersRequestState])
  const topUsersData = Array.isArray(topUsers?.data) ? topUsers.data : []

  const maxEnergyConsumption = Math.max(
    ...(topUsersData.map((item) => item.totalEnergyConsumption || 0) ?? [0]),
  )

  const energyUnitConfig = getEnergyUnit(maxEnergyConsumption)
  const TopUsersTableColumns = useMemo(
    () => [
      {
        accessorKey: 'name',
        enableEditing: false,
        size: 30,

        muiTableHeadCellProps: {
          sx: {
            backgroundColor: Colors.Analytics.AnalyticsDataCard,
            color: Colors.Analytics.AnalyticsDataGreyText,
          },
        },
        header:
          fetchTopUsersRequestState === RequestState.LOADING ? (
            <div>
              <IFSkeleton
                variant="text"
                width="120px"
                animation="none"
                height={20}
              />
            </div>
          ) : (
            <div className={Styles.Title}>{t('Analytics.Voucher')}</div>
          ),
        Cell: ({ row }) => {
          return row.index < topUsers?.data?.length ? (
            <div
              className={Styles.Name}
              onClick={() => {
                const userId = row.original._id

                let userFilters = [
                  {
                    type: 0,
                    title: null,
                    data: null,
                    initialValue: [],
                    value: [userId],
                    ref: { current: null },
                  },
                  {
                    type: 2,
                    title: 'Status',
                    data: ['Active', 'Inactive'],
                    initialValue: [],
                    value: [],
                    ref: { current: { isOpen: false } },
                  },
                ]

                if (admin.registeredCountries.length > 1) {
                  userFilters.push({
                    type: 2,
                    title: 'Country',
                    data: admin.registeredCountries.map((c) => c.name),
                    field: 'Country',
                    value: [],
                    initialValue: [],
                  })
                }
                setUsersFilter(userFilters)
                history.push({
                  pathname: '/user',
                  search: `?filter=${encodeObjectToBase64(userFilters)}`,
                })
                navigate(
                  `/user/${userId}?filter=${encodeObjectToBase64(userFilters)}`,
                )
              }}
            >
              <IFText>{row.original.name}</IFText>
            </div>
          ) : failedRequest ? (
            <div>
              <ReportIcon
                width={28}
                height={28}
                style={{ color: Colors.red }}
              />
              <IFText>{t('IFTable.TryAgainText')}</IFText>
            </div>
          ) : (
            <IFSkeleton
              variant="text"
              width="120px"
              animation="none"
              height={20}
            />
          )
        },
      },
      {
        accessorKey: 'EnergyConsumption',
        enableEditing: false,
        size: 40,
        muiTableHeadCellProps: {
          align: 'center',
          sx: {
            backgroundColor: Colors.Analytics.AnalyticsDataCard,
            color: Colors.Analytics.AnalyticsDataGreyText,
            padding: '0 !important',
          },
        },
        muiTableBodyCellProps: {
          sx: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '0 !important',
          },
        },
        header:
          fetchTopUsersRequestState === RequestState.LOADING ? (
            <div>
              <IFSkeleton
                variant="text"
                width="120px"
                animation="none"
                height={20}
              />
            </div>
          ) : (
            <div className={Styles.WrapHeader}>
              {t('Analytics.EnergyConsumption')} ({energyUnitConfig.unit})
            </div>
          ),
        Cell: ({ row }) => {
          if (row.index < topUsers?.data?.length) {
            const rawValue = row.original.totalEnergyConsumption ?? 0

            const formattedValue = (
              rawValue / energyUnitConfig.divisor
            ).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })

            return (
              <div className={Styles.Header}>
                <IFText>{formattedValue}</IFText>
              </div>
            )
          }

          if (failedRequest) {
            return (
              <div>
                <ReportIcon
                  width={28}
                  height={28}
                  style={{ color: Colors.red }}
                />
                <IFText>{t('IFTable.TryAgainText')}</IFText>
              </div>
            )
          }

          return (
            <IFSkeleton
              variant="text"
              width="120px"
              animation="none"
              height={20}
            />
          )
        },
      },
      {
        accessorKey: 'NumberOfTransactions',
        enableEditing: false,
        size: 50,
        muiTableHeadCellProps: {
          align: 'center',
          sx: {
            backgroundColor: Colors.Analytics.AnalyticsDataCard,
            color: Colors.Analytics.AnalyticsDataGreyText,
            padding: '0 !important',
          },
        },
        muiTableBodyCellProps: {
          sx: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '0 !important',
          },
        },
        header:
          fetchTopUsersRequestState === RequestState.LOADING ? (
            <div>
              <IFSkeleton
                variant="text"
                width="120px"
                animation="none"
                height={20}
              />
            </div>
          ) : (
            <div>{t('Analytics.NumberOfTransactions')}</div>
          ),
        Cell: ({ row }) => {
          return row.index < topUsers?.data?.length ? (
            <div className={Styles.Header}>
              <IFText>{row.original.transactionCount}</IFText>
            </div>
          ) : failedRequest ? (
            <div>
              <ReportIcon
                width={28}
                height={28}
                style={{ color: Colors.red }}
              />
              <IFText>{t('IFTable.TryAgainText')}</IFText>
            </div>
          ) : (
            <IFSkeleton
              variant="text"
              width="120px"
              animation="none"
              height={20}
            />
          )
        },
      },
    ],
    [topUsers, fetchTopUsers, fetchTopUsersRequestState, failedRequest],
  )
  const loadMoreData = () => {
    if (fetchTopUsersRequestState === RequestState.LOADING) return
    fetchTopUsers(filter)
  }
  const fetchMoreOnBottomReached = useCallback(
    (event) => {
      if (event) {
        const { scrollHeight, scrollTop, clientHeight } = event
        if (scrollHeight - scrollTop - clientHeight < 400) {
          loadMoreData()
        }
      }
    },
    [loadMoreData],
  )

  return (
    <IFTable
      columns={TopUsersTableColumns}
      ref={tableRef}
      tableMaxHeight={486}
      filters={filter}
      tableRequestState={fetchTopUsersRequestState}
      fetchMoreOnBottomReached={fetchMoreOnBottomReached}
      data={topUsers.data}
      showSkeleton={
        fetchTopUsersRequestState === RequestState.LOADING ||
        (fetchTopUsersRequestState !== RequestState.SUCCEEDED &&
          topUsers?.data.length === 0)
      }
    />
  )
}

const mapStateToProps = (state) => ({
  topUsers: AnalyticsSelectors.getTopUsers(state),
  fetchTopUsersRequestState:
    AnalyticsSelectors.getFetchTopUsersRequestState(state),
  admin: AuthSelectors.getUser(state),
})

const mapDispatchToProps = (dispatch) => ({
  fetchTopUsers: (filter) => dispatch(AnalyticsActions.fetchTopUsers(filter)),
  setUsersFilter: (newFilter) =>
    dispatch(UserActions.setUsersFilter(newFilter)),
})

TopUsersCard.propTypes = {
  filter: PropTypes.object,
  topUsers: PropTypes.object,
  fetchTopUsers: PropTypes.func,
  fetchTopUsersRequestState: PropTypes.number,
  admin: PropTypes.object,
}

export default connect(mapStateToProps, mapDispatchToProps)(TopUsersCard)
